/**
 * Removes <b>, </b>, <i>, </i>, <strong>, </strong>, <em>, </em> tags from text.
 * Used for validation counts (characters, words) that should ignore formatting.
 */
export function stripFormattingTags(text: string): string {
  return text.replace(/<\/?(?:b|i|strong|em)>/gi, "");
}

// Pattern matching ALL allowed inline formatting tags (legacy + canonical).
const ALLOWED_TAG_REGEX = /<\/?(?:b|i|strong|em)>/gi;

/**
 * Normalizes legacy presentational tags to their semantic equivalents:
 * <b> → <strong>, <i> → <em> (and closing tags). Done so the rich-text
 * editor (TipTap default) and existing DB phrases converge on one form.
 */
function normalizeLegacyTags(html: string): string {
  return html
    .replace(/<b>/gi, "<strong>")
    .replace(/<\/b>/gi, "</strong>")
    .replace(/<i>/gi, "<em>")
    .replace(/<\/i>/gi, "</em>");
}

/**
 * Removes ALL HTML tags EXCEPT <strong>, <em> (and legacy <b>, <i> which are
 * normalized to <strong>/<em>). Prevents XSS when rendering phrase HTML.
 */
export function sanitizePhrase(html: string): string {
  // First, preserve allowed tags by replacing them with placeholders
  const preserved: string[] = [];
  const placeholder = "___SAFE_TAG_";
  const withPlaceholders = html.replace(ALLOWED_TAG_REGEX, (match) => {
    preserved.push(match);
    return `${placeholder}${preserved.length - 1}___`;
  });

  // Strip all remaining HTML tags
  const stripped = withPlaceholders.replace(/<[^>]*>/g, "");

  // Also escape any remaining angle brackets to prevent injection
  const escaped = stripped
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Restore preserved tags, normalizing legacy <b>/<i> → <strong>/<em>
  const placeholderRegex = new RegExp(`${placeholder}(\\d+)___`, "g");
  const restored = escaped.replace(placeholderRegex, (_, idx) =>
    normalizeLegacyTags(preserved[Number(idx)])
  );

  // Strip zero-width spaces (used as cursor anchors for bold/italic toggle)
  const noZws = restored.replace(/\u200B/g, "");

  // Normalize &nbsp; to regular space (contentEditable inserts these frequently)
  const noNbsp = noZws.replace(/&nbsp;/gi, " ");

  // Normalize spaces around formatting tags:
  // Move trailing spaces from inside closing tags to outside.
  // Move leading spaces from inside opening tags to outside.
  // Loop to handle nested tags: <strong><em>text </em></strong> needs two passes.
  let spacesOut = noNbsp;
  let prevSpaces;
  do {
    prevSpaces = spacesOut;
    spacesOut = spacesOut
      .replace(/(\s+)(<\/(?:strong|em)>)/gi, "$2$1")
      .replace(/(<(?:strong|em)>)(\s+)/gi, "$2$1");
  } while (spacesOut !== prevSpaces);

  // Remove empty formatting tags (loop handles nested like <strong><em></em></strong>)
  let cleaned = spacesOut;
  let prev;
  do {
    prev = cleaned;
    cleaned = cleaned.replace(/<(strong|em)><\/\1>/gi, "");
  } while (cleaned !== prev);

  return cleaned;
}

/**
 * Applies a text transform function (e.g. uppercase, lowercase) to text content only,
 * preserving formatting tag positions.
 */
export function preserveTagsTransform(
  html: string,
  fn: (text: string) => string
): string {
  // Extract tags and their positions
  const tagRegex = new RegExp(ALLOWED_TAG_REGEX.source, "gi");
  const tags: { tag: string; textIndex: number }[] = [];
  let textOnly = "";
  let lastIndex = 0;
  let match;

  while ((match = tagRegex.exec(html)) !== null) {
    // Add text before this tag
    textOnly += html.slice(lastIndex, match.index);
    tags.push({ tag: match[0], textIndex: textOnly.length });
    lastIndex = match.index + match[0].length;
  }
  // Add remaining text
  textOnly += html.slice(lastIndex);

  // Apply transform to text only
  const transformed = fn(textOnly);

  // Re-insert tags at their positions
  let result = "";
  let tagIdx = 0;
  for (let i = 0; i <= transformed.length; i++) {
    while (tagIdx < tags.length && tags[tagIdx].textIndex === i) {
      result += tags[tagIdx].tag;
      tagIdx++;
    }
    if (i < transformed.length) {
      result += transformed[i];
    }
  }

  return result;
}
